import { Link, useNavigate } from 'react-router-dom';
import { getFreeRooms, getDeepRooms, getScaryRooms, getDailyRooms, getTimeLockedRooms, getGameRooms, getExperimentalRooms, rooms } from '@/data/rooms';
import { Clock, Target, Moon, Calendar, Sparkles, Map, Search, Brain, Shuffle, Star, Settings, UserPlus, Sunrise, Trophy, Bookmark, Diamond, Route, Heart, BookOpen, Bot } from 'lucide-react';
import { useWhatsNew } from '@/hooks/useWhatsNew';
import WhatsNewBadge from '@/components/WhatsNewBadge';
import { useState, useEffect, useMemo } from 'react';
import AmbientMusicControl from '@/components/AmbientMusicControl';
import RoomAudio from '@/components/RoomAudio';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import StreakDisplay from '@/components/StreakDisplay';
import RoomCard from '@/components/RoomCard';
import RoomProgressIndicator from '@/components/RoomProgressIndicator';
import AIGuide from '@/components/AIGuide';
import DailyChallengesCard from '@/components/DailyChallengesCard';
import WeeklySummaryCard from '@/components/WeeklySummaryCard';
import ThemeExclusiveRoomsSection from '@/components/ThemeExclusiveRoomsSection';
import AlternateModeRoomsSection from '@/components/AlternateModeRoomsSection';
import DailyHighlight from '@/components/DailyHighlight';
import RecommendedRooms from '@/components/RecommendedRooms';
import ExitButton from '@/components/ExitButton';
import MoodSelector from '@/components/MoodSelector';
import RoomChains from '@/components/RoomChains';
import RecentlyVisited from '@/components/RecentlyVisited';
import StatsBar from '@/components/StatsBar';
import { startSession } from '@/components/ExitRitualOverlay';
import { useProgression } from '@/hooks/useProgression';
import { useSecretDiscovery, useIdleDetection } from '@/hooks/useSecretDiscovery';
import { useTimeLocked, getTimeLockedRoomAvailability } from '@/hooks/useTimeLocked';
import { useBookmarks } from '@/hooks/useBookmarks';
import { useRoomRatings } from '@/hooks/useRoomRatings';
import { useLanguage } from '@/i18n/LanguageContext';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';
import { toast } from '@/hooks/use-toast';

const Hallway = () => {
  const [visible, setVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'games' | 'experimental' | 'deep' | 'dark' | 'bookmarked'>('all');
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { state, getChallenges, getFragmentCount, isRoomCompleted, fragmentsNeeded } = useProgression();
  const { state: secretState, unlockIdleRoom } = useSecretDiscovery();
  const { t, dir, isRTL } = useLanguage();
  const timeState = useTimeLocked();
  const { hasNewUpdates, markAsSeen } = useWhatsNew();
  const { bookmarks, bookmarkCount } = useBookmarks();
  const { getTopRatedRoomIds } = useRoomRatings();

  useIdleDetection(() => {
    if (!secretState.idleRoomUnlocked) {
      unlockIdleRoom();
      toast({ title: t('hidden_discovered'), description: t('stillness_revealed') });
    }
  }, 120000);

  useKeyboardNavigation({ enableGlobalShortcuts: true });

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    startSession();
    return () => clearTimeout(timer);
  }, []);

  // Track scroll for sticky header shadow
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const freeRooms = getFreeRooms().filter(r => r.category !== 'daily' && r.category !== 'meta' && r.category !== 'games' && r.category !== 'experimental');
  const deepRooms = getDeepRooms();
  const scaryRooms = getScaryRooms();
  const dailyRooms = getDailyRooms();
  const timeLockedRooms = getTimeLockedRooms();
  const gameRooms = getGameRooms();
  const experimentalRooms = getExperimentalRooms();
  const freeFragments = getFragmentCount('free');

  const allRoomPaths = useMemo(() => [
    ...freeRooms, ...gameRooms, ...experimentalRooms, ...deepRooms
  ].map(r => r.path), [freeRooms, gameRooms, experimentalRooms, deepRooms]);

  const enterRandomRoom = () => {
    const randomIndex = Math.floor(Math.random() * allRoomPaths.length);
    navigate(allRoomPaths[randomIndex]);
  };

  const bookmarkedRooms = useMemo(() => {
    return rooms.filter(room => bookmarks.includes(room.id));
  }, [bookmarks]);

  const mostLovedRooms = useMemo(() => {
    const topIds = getTopRatedRoomIds(6);
    return rooms.filter(room => topIds.includes(room.id));
  }, []);

  const filteredRooms = useMemo(() => {
    let roomsList = freeRooms;
    if (activeTab === 'games') roomsList = gameRooms;
    else if (activeTab === 'experimental') roomsList = experimentalRooms;
    else if (activeTab === 'deep') roomsList = deepRooms;
    else if (activeTab === 'dark') roomsList = scaryRooms;
    else if (activeTab === 'bookmarked') roomsList = bookmarkedRooms;

    if (!searchQuery) return roomsList;
    return roomsList.filter(room =>
      room.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      room.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [activeTab, searchQuery, freeRooms, gameRooms, experimentalRooms, deepRooms, scaryRooms, bookmarkedRooms]);

  const tabs = [
    { id: 'all', label: t('all_categories'), count: freeRooms.length },
    ...(bookmarkCount > 0 ? [{ id: 'bookmarked', label: t('bookmarked_rooms'), count: bookmarkCount, color: 'text-amber-400', icon: Bookmark }] : []),
    { id: 'games', label: t('game_rooms'), count: gameRooms.length, color: 'text-orange-400' },
    { id: 'experimental', label: t('experimental_rooms'), count: experimentalRooms.length, color: 'text-pink-400' },
    { id: 'deep', label: t('deep_rooms'), count: deepRooms.length, color: 'text-purple-400' },
    ...(state.unlockedHorror ? [{ id: 'dark', label: t('dark_rooms'), count: scaryRooms.length, color: 'text-red-400' }] : []),
  ];

  // Section divider component
  const SectionDivider = () => (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2">
      <div className="h-px bg-gradient-to-r from-transparent via-border/30 to-transparent" />
    </div>
  );

  return (
    <div className="min-h-screen bg-background scroll-smooth" dir={dir}>
      <RoomAudio category="default" />
      <AIGuide />

      {/* Clean Header */}
      <header className={`sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b border-border/10 transition-shadow duration-300 ${scrolled ? 'shadow-lg shadow-background/50' : ''}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="text-xs text-muted-foreground/40 hover:text-foreground/60 transition-colors">
              {t('back')}
            </Link>
            <div className="flex items-center gap-3">
              <StreakDisplay variant="compact" />
              <LanguageSwitcher />
              <AmbientMusicControl />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className={`py-12 px-4 sm:px-6 transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl font-extralight tracking-tight mb-3">{t('hallway')}</h1>
          <p className="text-muted-foreground/40 text-sm max-w-md mx-auto">
            {state.completedRooms.length === 0 
              ? t('choose_direction') || 'Choose a direction. Some doors respond.' 
              : `${state.completedRooms.length} ${t('rooms_explored') || 'rooms explored'} · ${state.fragments.length} ${t('fragments')}`}
          </p>
          
          {/* Progress Bar */}
          <div className="mt-6 max-w-xs mx-auto">
            <div className="flex justify-between text-[10px] text-muted-foreground/30 mb-1">
              <span>{freeFragments}/{fragmentsNeeded.free} {t('fragments')}</span>
              <span>{state.bossRoomsUnlocked.free ? t('boss_unlocked') : ''}</span>
            </div>
            <div className="h-1 bg-border/20 rounded-full overflow-hidden">
              <div className="h-full bg-foreground/30 transition-all" style={{ width: `${Math.min(100, (freeFragments / fragmentsNeeded.free) * 100)}%` }} />
            </div>
          </div>
        </div>
      </section>

      {/* Mood Selector */}
      <section className="px-4 sm:px-6 pb-6">
        <div className="max-w-6xl mx-auto">
          <MoodSelector />
        </div>
      </section>

      {/* Daily Highlight */}
      <section className="px-4 sm:px-6 pb-8">
        <div className="max-w-6xl mx-auto">
          <DailyHighlight />
        </div>
      </section>

      {/* Recently Visited */}
      <section className="px-4 sm:px-6 pb-6">
        <div className="max-w-6xl mx-auto">
          <RecentlyVisited />
        </div>
      </section>

      <SectionDivider />

      {/* Mini Stats Bar */}
      <section className="px-4 sm:px-6 py-6">
        <div className="max-w-6xl mx-auto">
          <StatsBar />
        </div>
      </section>

      <SectionDivider />

      {/* Quick Links - Reorganized into Explore + Your Space */}
      <section className="px-4 sm:px-6 py-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Random Room Button */}
          <button
            onClick={enterRandomRoom}
            className="w-full p-4 border border-border/30 hover:border-foreground/30 bg-gradient-to-r from-foreground/5 to-transparent transition-all group rounded-xl flex items-center justify-center gap-3 hover:-translate-y-0.5 hover:shadow-md"
          >
            <Shuffle className="w-5 h-5 text-muted-foreground/50 group-hover:text-foreground/80 transition-colors" />
            <span className="text-sm font-light group-hover:text-foreground transition-colors">{t('random_room') || 'Random Room'}</span>
          </button>

          {/* Row 1: Explore */}
          <div>
            <h3 className="text-[10px] uppercase tracking-widest text-muted-foreground/30 mb-3 flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-muted-foreground/20" />
              {t('explore') || 'Explore'}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Link to="/companion" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl bg-gradient-to-br from-violet-500/5 to-transparent hover:-translate-y-0.5">
                <Bot className={`w-4 h-4 text-violet-400/60 group-hover:text-violet-400 mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('ai_companion') || 'Companion'}</p>
              </Link>
              <Link to="/room/ai-reflection-room" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl hover:-translate-y-0.5">
                <Brain className={`w-4 h-4 text-muted-foreground/40 group-hover:text-foreground/60 mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('ai_reflection') || 'AI Reflection'}</p>
              </Link>
              <Link to="/room/imagine-yourself" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl bg-gradient-to-br from-primary/5 to-transparent hover:-translate-y-0.5">
                <Sparkles className={`w-4 h-4 text-primary/60 group-hover:text-primary mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('imagine_room') || 'Imagine'}</p>
              </Link>
              <Link to="/room/dream-visualization" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl bg-gradient-to-br from-primary/5 to-transparent hover:-translate-y-0.5">
                <Moon className={`w-4 h-4 text-primary/60 group-hover:text-primary mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('dream_room') || 'Dreams'}</p>
              </Link>
              <Link to="/room/future-self" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl bg-gradient-to-br from-primary/5 to-transparent hover:-translate-y-0.5">
                <Sunrise className={`w-4 h-4 text-primary/60 group-hover:text-primary mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('future_room') || 'Future'}</p>
              </Link>
              <Link to="/constellation" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl hover:-translate-y-0.5">
                <Map className={`w-4 h-4 text-muted-foreground/40 group-hover:text-foreground/60 mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('constellation_map')}</p>
              </Link>
            </div>
          </div>

          {/* Row 2: Your Space */}
          <div>
            <h3 className="text-[10px] uppercase tracking-widest text-muted-foreground/30 mb-3 flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-muted-foreground/20" />
              {t('your_space') || 'Your Space'}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Link to="/stats" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl hover:-translate-y-0.5">
                <Target className={`w-4 h-4 text-muted-foreground/40 group-hover:text-foreground/60 mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('your_stats')}</p>
              </Link>
              <Link to="/collection" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl bg-gradient-to-br from-amber-500/5 to-transparent hover:-translate-y-0.5">
                <Diamond className={`w-4 h-4 text-amber-400/60 group-hover:text-amber-400 mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('fragment_collection') || 'Collection'}</p>
              </Link>
              <Link to="/achievements" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl bg-gradient-to-br from-primary/5 to-transparent hover:-translate-y-0.5">
                <Trophy className={`w-4 h-4 text-primary/60 group-hover:text-primary mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('achievements') || 'Achievements'}</p>
              </Link>
              <Link to="/journeys" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl bg-gradient-to-br from-indigo-500/5 to-transparent hover:-translate-y-0.5">
                <Route className={`w-4 h-4 text-indigo-400/60 group-hover:text-indigo-400 mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('guided_journeys') || 'Journeys'}</p>
              </Link>
              <Link to="/journal" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl bg-gradient-to-br from-emerald-500/5 to-transparent hover:-translate-y-0.5">
                <BookOpen className={`w-4 h-4 text-emerald-400/60 group-hover:text-emerald-400 mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('journal') || 'Journal'}</p>
              </Link>
              <Link to="/settings" className="p-4 border border-border/20 hover:border-foreground/20 transition-all group rounded-xl hover:-translate-y-0.5">
                <Settings className={`w-4 h-4 text-muted-foreground/40 group-hover:text-foreground/60 mb-2 ${isRTL ? 'rtl-flip' : ''}`} />
                <p className="text-sm font-light">{t('settings') || 'Settings'}</p>
              </Link>
            </div>
          </div>

          {/* Row 3: More (smaller) */}
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/room/invite-friend-room" className="px-4 py-2 border border-border/10 hover:border-border/30 rounded-lg text-xs text-muted-foreground/40 hover:text-muted-foreground transition-all flex items-center gap-2">
              <UserPlus className="w-3 h-3" />
              {t('shared_room') || 'Shared Room'}
            </Link>
            <Link 
              to="/updates" 
              onClick={markAsSeen}
              className="px-4 py-2 border border-border/10 hover:border-border/30 rounded-lg text-xs text-muted-foreground/40 hover:text-muted-foreground transition-all flex items-center gap-2 relative"
            >
              <Star className="w-3 h-3" />
              <WhatsNewBadge show={hasNewUpdates} />
              {t('updates') || 'Updates'}
              {hasNewUpdates && (
                <span className="text-[9px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                  {t('new') || 'NEW'}
                </span>
              )}
            </Link>
            <Link to="/support" className="px-4 py-2 border border-border/10 hover:border-border/30 rounded-lg text-xs text-muted-foreground/40 hover:text-muted-foreground transition-all flex items-center gap-2">
              <Sparkles className="w-3 h-3" />
              {t('support') || 'Support'}
            </Link>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Most Loved Rooms */}
      {mostLovedRooms.length > 0 && (
        <>
          <section className="px-4 sm:px-6 py-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xs uppercase tracking-widest text-muted-foreground/40 mb-4 flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-muted-foreground/20" />
                <Heart className="w-3 h-3" /> {t('most_loved_rooms') || 'Most Loved Rooms'}
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {mostLovedRooms.map((room) => (
                  <RoomCard key={room.id} room={room} variant="featured" />
                ))}
              </div>
            </div>
          </section>
          <SectionDivider />
        </>
      )}

      {/* Recommended Rooms Section */}
      <section className="px-4 sm:px-6 py-8">
        <div className="max-w-6xl mx-auto">
          <RecommendedRooms />
        </div>
      </section>

      <SectionDivider />

      {/* Engagement Section */}
      <section className="px-4 sm:px-6 py-8">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StreakDisplay variant="full" className="rounded-xl" />
          <DailyChallengesCard />
          <WeeklySummaryCard variant="full" className="rounded-xl" />
        </div>
      </section>

      {/* Room Chains */}
      <section className="px-4 sm:px-6 py-8">
        <div className="max-w-6xl mx-auto">
          <RoomChains />
        </div>
      </section>

      <SectionDivider />

      {/* Search & Tabs */}
      <section className={`px-4 sm:px-6 pb-6 pt-4 sticky top-[73px] z-20 bg-background/95 backdrop-blur-sm transition-shadow duration-300 ${scrolled ? 'shadow-md shadow-background/30' : ''}`}>
        <div className="max-w-6xl mx-auto space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/30 ${isRTL ? 'right-4' : 'left-4'}`} />
            <input
              type="text"
              placeholder={t('search_rooms')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full py-3 bg-card/30 border border-border/20 rounded-xl text-sm placeholder:text-muted-foreground/30 focus:border-foreground/20 focus:outline-none transition-all ${isRTL ? 'pr-11 pl-4' : 'pl-11 pr-4'}`}
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`relative px-4 py-2 text-xs whitespace-nowrap rounded-full border transition-all duration-300 ease-out ${
                  activeTab === tab.id 
                    ? `border-foreground/30 bg-foreground/10 ${tab.color || 'text-foreground'}` 
                    : 'border-border/20 text-muted-foreground/50 hover:border-border/40'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-foreground/10' : 'bg-border/20'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Room Grid */}
      <section className="px-4 sm:px-6 pb-12">
        <div className="max-w-6xl mx-auto">
          {filteredRooms.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-muted-foreground/40 text-sm">{t('no_results')}</p>
              <button onClick={() => { setSearchQuery(''); setActiveTab('all'); }} className="mt-2 text-xs text-muted-foreground/30 hover:text-muted-foreground">
                {t('clear_filters')}
              </button>
            </div>
          ) : (
            <div 
              key={activeTab} 
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in"
            >
              {filteredRooms.map((room, index) => (
                <div 
                  key={room.id}
                  className="transform transition-all duration-300 ease-out"
                  style={{ 
                    animationDelay: `${index * 40}ms`,
                    animation: 'fadeSlideIn 0.4s ease-out forwards',
                    opacity: 0
                  }}
                >
                  <RoomCard room={room} animationDelay={index * 40} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Alternate Mode Exclusive Rooms */}
      <AlternateModeRoomsSection />

      {/* Theme-Exclusive Rooms */}
      <ThemeExclusiveRoomsSection />

      {/* Time-Locked & Daily Rooms */}
      <section className="px-4 sm:px-6 pb-12">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Time-Locked */}
          <div>
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground/40 mb-4 flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-muted-foreground/20" />
              <Clock className="w-3 h-3" /> {t('time_locked')}
            </h2>
            <div className="grid sm:grid-cols-3 gap-3">
              {timeLockedRooms.map((room) => {
                const availability = getTimeLockedRoomAvailability(room.id, timeState);
                return (
                  <Link key={room.id} to={room.path} className={`p-4 border rounded-xl transition-all ${availability.available ? 'border-green-500/30 bg-green-500/5' : 'border-border/20 opacity-60'}`}>
                    <h3 className="font-light text-sm">{t(`room_${room.id}`) !== `room_${room.id}` ? t(`room_${room.id}`) : room.title}</h3>
                    <p className="text-muted-foreground/40 text-xs mt-1">{availability.available ? t('available_now') : room.subtitle}</p>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Daily */}
          <div>
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground/40 mb-4 flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-muted-foreground/20" />
              <Calendar className="w-3 h-3" /> {t('daily_rooms')}
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {dailyRooms.map((room) => (
                <RoomCard key={room.id} room={room} variant="featured" />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 sm:px-6 py-10 border-t border-border/10">
        <div className="max-w-6xl mx-auto text-center space-y-6">
          <RoomProgressIndicator variant="bar" />
          
          {/* Exit Button - Centered and prominent */}
          <div className="flex justify-center py-4">
            <div className="animate-breathe">
              <ExitButton />
            </div>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/about" className="text-xs text-muted-foreground/30 hover:text-muted-foreground transition-colors">{t('what_is_this')}</Link>
          </div>
          <p className="text-[10px] text-muted-foreground/20">
            {state.completedRooms.length} {t('rooms_explored') || 'rooms explored'} · {state.fragments.length} {t('fragments')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Hallway;
